import { getImageProps } from 'next/image';

/**
 * Imagen con "art direction" (una fuente para móvil y otra para escritorio)
 * resuelta por CSS con <picture>, en vez de leer el user-agent en el
 * servidor. Así la página puede ser estática/ISR y cachearse en la CDN.
 *
 * Con `priority` emite dos <link rel="preload"> con `media`, de modo que cada
 * dispositivo precarga solo su variante (importante para el LCP de los heros).
 */
export default function ResponsiveImage({
  mobileSrc,
  desktopSrc,
  alt,
  sizes = '100vw',
  priority = false,
  className = '',
  quality,
  unoptimized = false,
  breakpoint = 768,
  fill = true,
  width,
  height,
}: {
  mobileSrc: string;
  desktopSrc: string;
  alt: string;
  sizes?: string;
  priority?: boolean;
  className?: string;
  quality?: number;
  /** Sirve el fichero original tal cual, sin pasar por /_next/image. */
  unoptimized?: boolean;
  /** Ancho mínimo (px) a partir del cual se usa la imagen de escritorio. */
  breakpoint?: number;
  fill?: boolean;
  width?: number;
  height?: number;
}) {
  const base = fill
    ? {
        alt,
        sizes,
        priority,
        quality,
        unoptimized,
        className,
        fill: true as const,
      }
    : { alt, sizes, priority, quality, unoptimized, className, width, height };

  const { props: desktop } = getImageProps({ ...base, src: desktopSrc });
  const { props: mobile } = getImageProps({ ...base, src: mobileSrc });
  // Sin optimizar, next/image no genera srcSet: se usa la URL del original.
  const mobileSrcSet = mobile.srcSet ?? mobile.src;
  const desktopSrcSet = desktop.srcSet ?? desktop.src;

  const mobileMedia = `(max-width: ${breakpoint - 1}px)`;
  const desktopMedia = `(min-width: ${breakpoint}px)`;
  const highPriority = { fetchPriority: 'high' } as Record<string, string>;

  return (
    <>
      {priority && (
        <>
          <link
            rel="preload"
            as="image"
            media={mobileMedia}
            imageSrcSet={mobileSrcSet}
            imageSizes={mobile.sizes}
            {...highPriority}
          />
          <link
            rel="preload"
            as="image"
            media={desktopMedia}
            imageSrcSet={desktopSrcSet}
            imageSizes={desktop.sizes}
            {...highPriority}
          />
        </>
      )}
      <picture>
        <source
          media={mobileMedia}
          srcSet={mobileSrcSet}
          sizes={mobile.sizes}
        />
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img {...desktop} />
      </picture>
    </>
  );
}
